import { formatterCurrency } from "../../formatters";
import { PAYMENT_TYPE } from "../../gtm_script";
import { doesNotHaveValue, isPJ, parseFetchResponse } from "../../helper_functions";
import { addCorrId, tapAndLoadCorrId } from "../../utils/correlation_id_store";
import { purchaseCallbackFor } from "../../utils/gtm_callback";
import { useRecaptcha } from "../../utils/recaptcha_wrapper";

const NUPAY_STATUS_POLLING_TIME_LIMIT = 1000 * 60 * 5; // 5 min in ms
const NUPAY_STATUS_POLLING_INTERVAL = 1000 * 5; // 5s in ms

export function initNupay ({ setLoading, showPaymentError }) {
  function fetchTransactionStatus (clientInstantPaymentId, signal) {
    return fetch(`/client_instant_payments/${clientInstantPaymentId}`, { signal, headers: addCorrId() })
      .then(tapAndLoadCorrId)
      .then(async (response) => {
        if (response.ok) {
          const { status, error } = await response.json();
          return { status, error };
        }
        return { status: undefined, error: await response.text() };
      })
      .catch((error) => {
        return { status: undefined, error: { type: "requestError", error } };
      });
  }

  let cancelPaymentValidationLoop;

  function closeDialog () {
    if (cancelPaymentValidationLoop) {
      cancelPaymentValidationLoop();
    }

    const dialog = document.getElementById("nupay-dialog");

    dialog.close();
  }

  document.getElementById("nupay-dialog")
    .querySelector("button.close-dialog")
    .addEventListener("click", closeDialog);

  document.getElementById("nupay-dialog").addEventListener("cancel", (event) => {
    event.preventDefault();
    closeDialog();
  });

  function pollTransactionConfirmation (clientInstantPaymentId, expirationTime = null) {
    if (cancelPaymentValidationLoop) {
      cancelPaymentValidationLoop();
      cancelPaymentValidationLoop = undefined;
    }

    let resolveAsPending;

    const controller = new AbortController();

    let timeoutId;

    const promise = new Promise((resolve, reject) => {
      const expirationDelta = expirationTime ? expirationTime * 1000 : NUPAY_STATUS_POLLING_TIME_LIMIT;
      const timeoutLimit = Date.now() + expirationDelta;
      resolveAsPending = () => {
        resolve("pending");
      };
      function executeRequest () {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(async () => {
          const expired = Date.now() > timeoutLimit;
          const { status, error } = await fetchTransactionStatus(clientInstantPaymentId, controller.signal);
          if (error) {
            promise.cancel(error);
            return reject(error);
          }
          if (status !== "pending") {
            return resolve(status);
          }
          if (expired) {
            return resolve("pending");
          }
          executeRequest();
        }, NUPAY_STATUS_POLLING_INTERVAL);
      }
      executeRequest();
    });
    promise.cancel = (failed) => {
      if (!failed && resolveAsPending) {
        resolveAsPending();
      }
      clearTimeout(timeoutId);
      controller.abort();
    };
    cancelPaymentValidationLoop = promise.cancel;
    return promise;
  }

  function showDialog (clientInstantPaymentId, expiresIn) {
    setLoading(true);
    showPaymentError("");

    const dialog = document.getElementById("nupay-dialog");

    dialog.showModal();

    return pollTransactionConfirmation(clientInstantPaymentId, expiresIn * 1000);
  }

  return {
    showDialog,
    closeDialog
  };
}

export function setupNupayPayment ({
  clientForm,
  productForm,
  couponForm,
  bumpForm,
  formElements,
  formHelpers,
  showPaymentError,
  setLoading: baseSetLoading,
  gtm,
  setPaymentMethodSubmitHandler,
  onSuccess,
  onPending,
  onError,
  MPMInstance,
  metadata: { mode = false, showNupayFields = false } = {},
}) {
  const setLoading = (state) => baseSetLoading(state, {
    label: formElements.submitButtonLabel,
    loading: formElements.loadingIndicator
  });

  const { showDialog, closeDialog } = initNupay({ setLoading, showPaymentError });

  const nupayTotalAmountSpan = document.getElementById("nupay_total_amount");

  let totalAmountOperators = [];

  function updateTotalAmount() {
    const baseAmount = couponForm?.fixedCoupon 
      ? formElements.getAmountAndCouponInformation({ formatted: false }).total_amount
      : formElements.getProductInfo({ formatted: false }).unitAmount;
    const baseCents = (+baseAmount) * 100;
    const cents = totalAmountOperators.reduce((a, c, i, arr) => c(a, i, arr), baseCents);
    const amount = formatterCurrency.format(cents / 100);
    nupayTotalAmountSpan.innerText = `Valor ${amount} em até 24x`;
  }

  if (couponForm && !couponForm.fixedCoupon) {
    let coupon
    function sumCouponInNupayTotalAmount(value) {
      if (!coupon) return value

      return coupon?.discountedAmount ? +coupon.discountedAmount : value;
    }
    totalAmountOperators.unshift(sumCouponInNupayTotalAmount)
    couponForm.addCouponChangeListener((state) => {
      coupon = state;
      updateTotalAmount();
    });
  }

  if (bumpForm.enabled()) {
    let bumpState
    function sumBumpInNupayTotalAmount(value) {
      if (!bumpState?.active) return value

      return value + bumpState.value
    }
    totalAmountOperators.push(sumBumpInNupayTotalAmount);
    bumpForm.addChangeListener(function nupayBumpChangeListener(state) {
      bumpState = state;
      updateTotalAmount();
    })
  }

  if (mode === 'oto' && showNupayFields) {
    MPMInstance.addListener((paymentMethod) => {
      const isRequired = paymentMethod === 'nupay';

      formHelpers.disableCpfCnpjFields(!isRequired);
      clientForm.setPersonalPhoneNumberRequired(isRequired);
    })
  }

  async function createOTONupayPayment ({
    authenticity_token,
    clientInfo,
  }) {
    const gr_action = 'oto_nupay_charge';
    const gr_token = await useRecaptcha(gr_action);
    const sendDocument = document.getElementById('client_payment_use_saved_document')?.value != 'true'
    const sendPhone = document.getElementById('client_payment_use_saved_phone')?.value != 'true'

    const body = {
      gr_action,
      gr_token,
      authenticity_token,
      provider: "malga",
      payment_method: "nupay",
    }

    if (sendDocument) {
      body.document = clientInfo.document;
    }

    if (sendPhone) {
      body.phone_number = clientInfo.phone;
    }

    return fetch('/client_payments/oto', {
      method: 'POST',
      headers: addCorrId(new Headers({ 'Content-Type': 'application/json' })),
      body: JSON.stringify(body),
    })
    .then(tapAndLoadCorrId)
    .then((response) => {
      if (!response.ok) {
        throw new Error('Erro ao criar pagamento');
      }
      return response;
    })
    .then(parseFetchResponse)
    .catch((error) => {
      console.trace({ error });

      return null;
    });
  }

  async function createClientInstantPayment ({
    authenticity_token,
    clientInfo,
    product_id,
    paymentInfo,
  }) {
    const gr_action = 'nupay_charge';
    const gr_token = await useRecaptcha(gr_action);
    const body = {
      gr_action,
      gr_token,
      authenticity_token,
      provider: "malga",
      payment_method: "nupay",
      product_id,
      client_payment: {
        product_id,
        installments_count: 1,
        provider: "malga",
        payment_type: "nupay"
      },
      order_bump_active: bumpForm.enabled() && bumpForm.active(),
      coupons: formElements.getAmountAndCouponInformation()
    };

    body.client_payment = {
      ...body.client_payment,
      product_id,
      name: clientInfo.name,
      email: clientInfo.email,
      is_pj: clientInfo.is_pj,
      is_foreigner: clientInfo.is_foreigner,
      document: clientInfo.document,
      phone_number: clientInfo.phone_number,
      zip_code: clientInfo.zip_code,
      whatsapp_accept: clientInfo.whatsapp_accept,
      coupon_id: paymentInfo.coupon_id,
      amount_off: paymentInfo.coupon_amount_off,
      percent_off: paymentInfo.counpon_percent_off,
      discount_amount: paymentInfo.discount_amount,
      amount: paymentInfo.subtotal_amount,
      total_amount: paymentInfo.total_amount
    };

    /**
     * @type {{
     *  body: {
     *  id: string;
     *  provider: string;
     *  amount: number;
     *  stripe_customer: { id: string; };
     *  stripe_payment_intent: { id: string, client_secret: string; };
     *  }
     * }}
     */
    return fetch(
      "/client_instant_payments",
      {
        method: "POST",
        headers: addCorrId(new Headers({ "Content-Type": "application/json" })),
        body: JSON.stringify(body)
      }
    )
      .then(tapAndLoadCorrId)
      .then(parseFetchResponse)
      .catch((error) => {
        console.trace({ error });

        return null;
      });
    }

  // Submit the form to creates payment intent
  async function createNupayCharge () {
    const authenticity_token = formElements.authenticityTokenElement.value;
    const product_id = productForm.getId();
    const clientInfo = clientForm.getFormData();
    const paymentInfo = formElements.getAmountAndCouponInformation();

    const chargeStrategy = mode === "oto"
      ? createOTONupayPayment
      : createClientInstantPayment;

    const clientInstantPaymentResponse = await chargeStrategy({
      authenticity_token,
      product_id,
      clientInfo,
      paymentInfo,
    });

    if (!clientInstantPaymentResponse) {
      setLoading(false);

      showPaymentError(
        "Ocorreu um erro. Verifique os dados preenchidos e tente novamente em alguns minutos"
      );

      return;
    }

    const {
      body: {
        id: clientInstantPaymentId,
        product_id: productId,
        product_name,
        // unit_amount: unitAmount, // exists here but not being used
        amount: totalAmount,
        // transaction_id, // exists here but not being used
        // coupon_id, // exists here but not being used
        marketing_event_id: marketingEventIdentifier,
      }
    } = clientInstantPaymentResponse;

    const pixStatusUpdate = await showDialog(clientInstantPaymentId);

    if (pixStatusUpdate === "authorized") {
      const updateClientInstantPaymentResponse = await fetch(
        `/client_instant_payments/${clientInstantPaymentId}`,
        {
          method: "PATCH",
          headers: addCorrId(new Headers({ "Content-Type": "application/json" })),
          body: JSON.stringify({ authenticity_token })
        }
      )
        .then(tapAndLoadCorrId)
        .then(parseFetchResponse)
        .catch((error) => {
          console.error(
            "Failed to update client instant payment with confirmed payment intent:",
            error
          );
          return null;
        });
      if (
        updateClientInstantPaymentResponse === null ||
      updateClientInstantPaymentResponse.status !== 200
      ) {
        showPaymentError(
          "Ocorreu um erro ao confirmar o pagamento. Verifique os dados e tente novamente."
        );
        throw new Error(
          updateClientInstantPaymentResponse?.body ??
          "Failed to update client instant payment"
        );
      }
      setLoading(false);
      baseSetLoading(false);
      
    const responseBody = updateClientInstantPaymentResponse.body;
      if (typeof responseBody === 'string') {
        try {
          const parsedResponse = JSON.parse(responseBody);
          if (parsedResponse.status === 'oto_completed') {
            
            setTimeout(() => {
              window.parent.location.replace('https://pm3.com.br/');
            }, 2000);
            
            await gtm.registerPurchaseEvent(PAYMENT_TYPE.nupay, {
              coupon: paymentInfo.coupon_id,
              discount: paymentInfo.discount_amount,
              transactionId: clientInstantPaymentId,
              productId,
              productName: product_name,
              price: Number(totalAmount) / 100
            });
               
            return;
          }
        } catch (e) {
         
        }
      }
      
      await gtm.registerPurchaseEvent(
        PAYMENT_TYPE.nupay,
        {
          coupon: paymentInfo.coupon_id,
          discount: paymentInfo.discount_amount,
          transactionId: clientInstantPaymentId,
          productId,
          productName: product_name,
          price: Number(totalAmount) / 100
        },
        purchaseCallbackFor(marketingEventIdentifier)
      );
      onSuccess();
    } else if (["pre_authorized", "pending"].includes(pixStatusUpdate)) {
      setLoading(false);
      baseSetLoading(false);
      onPending();
    } else {
      baseSetLoading(false);
      setLoading(false);
      closeDialog();
      onError();
    }
  };

  function handleNupayChargeSubmission (_event) {
    showPaymentError("");

    if ((mode != 'oto' || showNupayFields) && !formHelpers.cpfCnpjValidator()) {
      showPaymentError(`${isPJ(clientForm.inputs.clientType.value) ? "CNPJ" : "CPF"} inválido`);
      return;
    }

    if (
      clientForm.isClientPj() &&
      doesNotHaveValue(clientForm.inputs.stateRegistrationInput) &&
      doesNotHaveValue(clientForm.inputs.municipalRegistrationInput)
    ) {
      if (formElements.stateRegistrationAlertMessageElement) {
        formElements.stateRegistrationAlertMessageElement.className = "";
        formElements.stateRegistrationAlertMessageElement.style =
          "color:#FF5757; font-weight:600;";
      }
      showPaymentError("Verifique as informações inseridas");
      return;
    }

    if (formElements.stateRegistrationAlertMessageElement) {
      formElements.stateRegistrationAlertMessageElement.className = "hidden";
    }
    baseSetLoading(true);
    setLoading(true);

    const productInfo = formElements.getProductInfo({ formatted: false });
    const paymentValues = formElements.getAmountAndCouponInformation({ formatted: false });

    gtm.registerCheckoutOptionEvent(PAYMENT_TYPE.nupay, {
      price: paymentValues.total_amount,
      discount: paymentValues.discount_amount,
      coupon: paymentValues.coupon_id,
      productName: productInfo.productName,
      productId: productInfo.productId
    });

    createNupayCharge()
      .catch((error) => {
        showPaymentError(error.message);
        baseSetLoading(false);
        setLoading(false);
        console.error(
          "Failed to create payment method",
          error.message,
          error?.stack ?? error
        );
        throw error;
      });
  };

  setPaymentMethodSubmitHandler("nupay", handleNupayChargeSubmission);
}
